import playerService from '../../../helpers/bitmovinPlayer';
import WaterMark from '../../../helpers/watermark';
import { toast } from '@intigral/shared';
import { __ } from '../../../translations';
import SMILHandler from '../../../helpers/SMILHandler';
import cdnHandler from '../../../helpers/CDNHandler';
import { playerTypes } from '@libs/constants';
import { log } from '@intigral/utils';
import { useState, useRef, useEffect, useCallback } from 'react';
import { HandleError } from '../types/IPlayer';
import { isDRMSupported } from '../../../helpers/isSupportedDRM';
import { isPlayerAlive, safePlayerPlay } from '../../../helpers/playerGuards';
import { type PlayerAPI, TimeMode } from 'bitmovin-player';
import h from '../../../helpers/common';

/**
 * Low latency sources are marked by a `lowlat` / `LL` path segment. Matching the pathname only
 * matters: the resolved URL carries a concurrency token and CDN query params, and a bare
 * `includes('LL')` on the whole URL would turn low latency on for a regular channel whenever an
 * uppercase `LL` happened to land inside a token.
 *
 * This is a stopgap. The player has no way to detect a low latency stream on its own — it acts on
 * whatever `live.lowLatency` config the app supplies — so the decision has to come from us, and a
 * CDN path convention is the only signal currently available client-side. Replace it with a proper
 * stream-metadata flag on the channel/stream response as soon as the backend exposes one; this is
 * the single call site.
 */
const LOW_LATENCY_PATH = /(^|[/_-])(lowlat|LL)([/_.-]|$)/;

export const isLowLatencySource = (streamingUrl?: string | null): boolean => {
    if (!streamingUrl) return false;

    try {
        return LOW_LATENCY_PATH.test(new URL(streamingUrl).pathname);
    } catch {
        return LOW_LATENCY_PATH.test(streamingUrl);
    }
};

/**
 * Set audio language from localstorage
 * @param {object} player
 * @param {string} language
 */
export const setAudioLanguage = (player, language) => {
    if (!player || !isPlayerAlive(player)) return;
    if (!player.getAvailableAudio() || player.getAvailableAudio().length === 1) return;
    let selectedAudio = null;
    // Enable the Audio track for a specific language.
    player.getAvailableAudio().map(function (x) {
        if (x.lang === language) {
            selectedAudio = x;
        }
    });

    if (selectedAudio) {
        player.setAudio(selectedAudio.id);
    }
};

/**
 * Callback of audio track change event
 * @param {*} event
 * @returns
 */
export const onAudioTrackChange = (event) => {
    if (!event || !event.targetAudio) return;
    const selectedAudioLang = event.targetAudio ? event.targetAudio.lang : null;
    if (selectedAudioLang !== null && !sessionStorage.getItem('ItemChanging')) {
        sessionStorage.setItem('selectedAudioLang', selectedAudioLang);
    }
    setTimeout(() => {
        sessionStorage.removeItem('ItemChanging');
    }, 1000);
};

/**
 * Set subtitle language
 * @param {object} player
 * @param {string} language
 */
export const setSubtitleLanguage = (player, language) => {
    // Channel switch can destroy the player before delayed subtitle setup runs.
    if (!player || !isPlayerAlive(player)) return;

    // Disable all text tracks that are currently active
    player.subtitles
        .list()
        .filter(function (x) {
            if (x.enabled === true) {
                return x;
            }
        })
        .forEach(function (x) {
            player?.subtitles.disable(x.id);
        });

    // Enable the text track for a specific language. Note: here i searched on the label, you can also do x.language for the ISO 3 letter language code
    const selectedLanguage = player?.subtitles?.list()?.find((item) => item?.lang === language);
    if (selectedLanguage) {
        player?.subtitles.enable(selectedLanguage.id);
    }
};

export const onSuccess = () => {
    if (document.querySelector('#video-container') !== null) {
        const errElements: any = document.querySelectorAll('.crash-error-live');
        for (let i = 0; i < errElements.length; i++) {
            errElements[i].style.display = 'none';
        }
    }
};

// export const setupWatermark = () => {
//     /** Watermark setup */
//     new WaterMark(playerService).setup();
// };

export const getChannelClasses = () => {
    try {
        const classes = document.querySelector('.cahnnel-stack').classList;
        return Object.values(classes).indexOf('show-cahnnel-stack');
    } catch (e) {
        return -1;
    }
};

// TODO: is this needed?
export const onTransitionEnd = (playerType) => {
    try {
        const controlBar: any = document.querySelector('.bmpui-ui-controlbar');
        const showChannel: any = document.querySelector('.show-channel');
        const movieTitle: any = document.querySelector('.movie-title');
        const customTitle: any = document.querySelector('.custom-title');
        const playerHeadingBlock: any = document.querySelector('#player-heading-block');

        if (controlBar && controlBar.className.includes('bmpui-hidden')) {
            if (playerType === 'LiveTV' && getChannelClasses() === -1 && showChannel) {
                showChannel.style.display = 'none';
            }

            if (playerType !== 'LiveTV' && movieTitle) {
                movieTitle.style.display = 'none';
            }

            if (getChannelClasses() === -1 && customTitle) {
                customTitle.style.visibility = 'hidden';
            }

            if (playerHeadingBlock && playerType !== 'LiveTV') {
                playerHeadingBlock.style.visibility = 'hidden';
            }
        } else {
            if (playerType === 'LiveTV' && showChannel) {
                showChannel.style.display = 'block';
            }

            if (playerType !== 'LiveTV' && movieTitle) {
                movieTitle.style.display = 'flex';
            }

            if (customTitle) {
                customTitle.style.visibility = 'visible';
            }

            if (playerHeadingBlock && playerType !== 'LiveTV') {
                playerHeadingBlock.style.visibility = 'visible';
            }
        }
    } catch (e) {
        log.error('Error updating UI elements based on player state:', e);
    }
};

export const seekTimestamp = (player) => {
    if (!player || !isPlayerAlive(player)) return;

    if (!window.location.search.includes('playbackTimestamp=') || player.isLive()) return;

    const duration = player?.getDuration();
    const timestamp = Number(new URLSearchParams(window.location.search).get('playbackTimestamp'));

    if (isNaN(timestamp) || timestamp < 0 || timestamp > duration) {
        toast(__('player_err_invalid_timestamp'), 'error');
        player.seek(0);
        return;
    }

    if (isFinite(duration)) {
        player.seek(timestamp < duration ? timestamp : 0);
    }

    safePlayerPlay(player);
};

/**
 * fetchStreamUrl
 * Fetches and resolves the stream URL using SMIL and CDN handlers
 * @param url - The raw catchup URL to resolve
 * @returns Promise with the resolved playable URL
 * @throws Error if URL resolution fails
 */
export const fetchStreamUrl = async (url: string, playerType: string, cdnSession?: unknown): Promise<string> => {
    const smilResponse = await SMILHandler.resolveUrl(url, undefined, cdnSession);
    if (!smilResponse) {
        throw new Error('Failed to resolve SMIL URL');
    }
    if (playerType === playerTypes.Trailer || playerType === playerTypes.PlayTrailer) {
        const newSmilResponse = smilResponse.replace('http:', 'https:');
        return newSmilResponse;
    }
    return smilResponse;
};

/**
 * A custom React hook that sets up an interval with a dynamic delay.
 * It encapsulates the interval logic and returns controls to start/stop it.
 *
 * @param {() => void} callback - The function to be executed on each interval.
 * @returns {{
 * start: (delay: number) => void,
 * stop: () => void,
 * isActive: boolean
 * }} - An object with controls for the interval.
 */
export const useQueueInterval = (callback: () => void) => {
    const [delay, setDelay] = useState<number | null>(null);
    const savedCallback = useRef<(() => void) | null>(null);

    // Remember the latest callback.
    useEffect(() => {
        savedCallback.current = callback;
    }, [callback]);

    // Set up the interval.
    useEffect(() => {
        const tick = () => {
            if (savedCallback.current) {
                savedCallback.current();
            }
        };
        if (delay !== null) {
            const id = setInterval(tick, delay);
            return () => clearInterval(id);
        }
    }, [delay]);

    const start = useCallback((newDelay: number) => {
        setDelay(newDelay);
    }, []);

    const stop = useCallback(() => {
        setDelay(null);
    }, []);

    return { start, stop, isActive: delay !== null };
};

export const handleError: HandleError = (error, onDestroy, setState) => {
    playerService.destroyPlayer();
    playerService.setPlayer(null);
    if (onDestroy) onDestroy();
    setState((prev) => ({
        ...prev,
        error: { status: true, title: error?.title, description: error.description },
    }));
};

export const checkDRMSupported = async (player, onDestroy, setState) => {
    if (player) {
        const drmSupported = await isDRMSupported(player);
        if (!drmSupported) {
            handleError(
                {
                    title: __('player_error_eme_not_supported_title'),
                    description: __('player_error_eme_not_supported_desc'),
                },
                onDestroy,
                setState
            );
            return false;
        }
    }
    return true;
};

/**
 * @param seekTimeCode - Seekbar target. When set, the bookmark is always sent with this time code (even past the
 * removal threshold) and deletion is left to the regular flow, so the last bookmark reflects the seek position.
 */
export const sendBookmarks = (
    contentOjb,
    playerService,
    playerType,
    addBookmark,
    removeBookmark,
    seekTimeCode?: number
): Promise<unknown> | null => {
    // On Live TV this can be invoked with no VOD asset (e.g. pause during teardown),
    // and the player may already be destroyed.
    if (!contentOjb || !playerService?.player || !isPlayerAlive(playerService.player)) return null;

    const bookmarkSettings = h.getBookmarkSettings();
    const bookmarkingRemovalPercentage = bookmarkSettings.removalPercentage;
    const bookmarkWithDelete = bookmarkSettings.withDelete;

    const object = contentOjb;
    const isSeek = typeof seekTimeCode === 'number' && Number.isFinite(seekTimeCode);
    const timeCode = isSeek
        ? Math.ceil(seekTimeCode)
        : playerService?.player?.getCurrentTime(TimeMode.RelativeTime) !== null
        ? Math.ceil(playerService?.player?.getCurrentTime(TimeMode.RelativeTime))
        : 0;
    const duration = Math.ceil(playerService?.player?.getDuration());
    const durationPercentage = bookmarkWithDelete ? bookmarkingRemovalPercentage : 100;
    const maximumDuration = (duration * durationPercentage) / 100;
    const content = contentOjb;

    if (playerType === playerTypes.LiveTV || playerType === playerTypes.Vod) {
        // check if there is ads and if the timecode is less than the ads duration then don't add bookmark
        const adsData = playerService?.getAdsData();
        if (adsData != null && timeCode < adsData?.duration / 1000) {
            return null;
        }
        if (timeCode > 0 && (isSeek || timeCode < maximumDuration) && playerService?.player?.getBufferedRanges()) {
            if (timeCode) {
                if (object.Id === content.Id) {
                    return addBookmark(content, timeCode, parseInt(content.duration, 10)) ?? null;
                }
            }
        }
        // RemoveBookmark;
        if (
            !isSeek &&
            bookmarkWithDelete &&
            timeCode > 0 &&
            timeCode >= maximumDuration &&
            playerService?.player?.getBufferedRanges()
        ) {
            // removeBookmark(content);
            removeBookmark({ bookmark: content });
        }
    }
    return null;
};

/**
 * Represents an extracted Ad Period from the DASH Manifest.
 */
export interface AdBlockSSAI {
    id: string;
    durationString: string;
    durationSeconds: number;
    startTimeSeconds: number;
    type: 'ad' | 'content';
    played: boolean;
}
